import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { PageHeader } from "@/components/shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Sparkles, Loader2, Target } from "lucide-react";

function Pane({ title, children, testid }) {
  return (
    <Card className="p-4" data-testid={testid}>
      <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-[#255058]">{title}</div>
      {children}
    </Card>
  );
}

function Group({ label, items }) {
  if (!(items || []).length) return null;
  return (
    <div><div className="mt-2 text-[10px] font-semibold uppercase text-muted-foreground">{label}</div>
      <ul className="space-y-0.5">{items.map((s, i) => <li key={i}>• {s}</li>)}</ul></div>
  );
}

export default function AIIntelligence() {
  const [cands, setCands] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [candId, setCandId] = useState("");
  const [jobId, setJobId] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [context, setContext] = useState(null);

  useEffect(() => {
    api.get("/candidates", { params: { page_size: 50 } }).then(({ data }) => setCands(data.items || [])).catch(() => {});
    api.get("/jobs", { params: { page_size: 50 } }).then(({ data }) => setJobs(data.items || [])).catch(() => {});
  }, []);

  const run = async () => {
    if (!candId || !jobId) { toast.error("Select a candidate and a job"); return; }
    setBusy(true); setResult(null);
    try {
      const ctx = await api.get("/ai/analysis", { params: { candidate_id: candId, job_id: jobId } });
      setContext(ctx.data);
      const { data } = await api.post("/ai/match", null, { params: { candidate_id: candId, job_id: jobId } });
      setResult(data);
    } catch (e) { toast.error(apiErr(e, "AI unavailable")); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <PageHeader title="NEXREQ Intelligence" subtitle="Explainable AI recruitment intelligence. AI assists — it never makes hiring decisions." />
      <Card className="p-5">
        <div className="flex items-center gap-2 mb-4 font-display font-semibold"><Target className="h-4 w-4 text-[#255058]" /> Candidate ↔ Job Match</div>
        <div className="grid sm:grid-cols-2 gap-3">
          <Select value={candId} onValueChange={setCandId}>
            <SelectTrigger data-testid="ai-candidate-select"><SelectValue placeholder="Select candidate" /></SelectTrigger>
            <SelectContent>{cands.map((c) => <SelectItem key={c.id} value={c.id}>{c.full_name} — {c.current_title}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={jobId} onValueChange={setJobId}>
            <SelectTrigger data-testid="ai-job-select"><SelectValue placeholder="Select job" /></SelectTrigger>
            <SelectContent>{jobs.map((j) => <SelectItem key={j.id} value={j.id}>{j.title} — {j.client_name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button onClick={run} disabled={busy} data-testid="ai-match-run" className="mt-4 gap-2 bg-[#255058] hover:bg-[#1c3d43] text-white">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Generate Match Score
        </Button>
      </Card>

      {context && (
        <div className="grid gap-4 xl:grid-cols-4" data-testid="ai-side-by-side">
          <Pane title="Resume" testid="ai-pane-resume">
            <div className="text-xs text-muted-foreground mb-2">{context.candidate.resume_filename || "No resume file"}</div>
            <div className="text-sm"><b>{context.candidate.full_name}</b> · {context.candidate.current_title || "Not Confirmed"}</div>
            <div className="text-xs text-muted-foreground">{context.candidate.location || "Not Confirmed"} · {context.candidate.years_experience ?? "Not Confirmed"} yrs</div>
            <div className="mt-2 flex flex-wrap gap-1">{(context.candidate.skills || []).map((s, i) => <Badge key={i} variant="secondary" className="text-[10px]">{s}</Badge>)}</div>
            <pre className="mt-3 max-h-64 overflow-auto whitespace-pre-wrap scrollbar-thin text-[11px] text-muted-foreground">{context.candidate.resume_text || "No resume text extracted."}</pre>
          </Pane>
          <Pane title="Job Description" testid="ai-pane-jd">
            <div className="text-sm"><b>{context.job.title}</b> · {context.job.client_name}</div>
            <div className="text-xs text-muted-foreground">{context.job.location || "Not Confirmed"} · {context.job.work_model} · {context.job.employment_type}</div>
            <div className="mt-2 flex flex-wrap gap-1">{(context.job.required_skills || []).map((s, i) => <Badge key={i} className="status-teal border-0 text-[10px]">{s}</Badge>)}</div>
            <div className="mt-2 text-xs">Experience: {context.job.required_experience || "Not Confirmed"}</div>
            <div className="text-xs">Certifications: {context.job.certifications || "Not Confirmed"}</div>
            <pre className="mt-3 max-h-64 overflow-auto whitespace-pre-wrap scrollbar-thin text-[11px] text-muted-foreground">{context.job.description || "No description."}</pre>
          </Pane>
          <Pane title="Screening" testid="ai-pane-screening">
            <dl className="space-y-1 text-xs">
              {Object.entries(context.screening).filter(([k]) => k !== "screening_answers").map(([k, v]) => (
                <div key={k} className="flex justify-between gap-3"><dt className="text-muted-foreground">{k.replace(/_/g, " ")}</dt>
                  <dd className={`text-right font-medium ${v === null || v === undefined || v === "" ? "italic text-muted-foreground" : ""}`}>{v === null || v === undefined || v === "" ? "Not Confirmed" : String(v)}</dd></div>
              ))}
            </dl>
            {(context.screening.screening_answers || []).map((a, i) => (
              <div key={i} className="mt-2 text-xs"><b>{a.question}</b><div className="text-muted-foreground">{a.answer || "Not Confirmed"}</div></div>
            ))}
          </Pane>
          <Pane title="AI Analysis" testid="ai-pane-analysis">
            {busy && <Loader2 className="h-4 w-4 animate-spin text-[#255058]" />}
            {result ? (
              <div className="space-y-2 text-xs">
                <div className="font-display text-2xl font-bold text-[#255058]">{result.score}%</div>
                <div className="text-sm font-medium">{result.verdict}</div>
                <p className="text-muted-foreground">{result.summary || result.reasoning}</p>
                <Group label="Strengths" items={result.strengths} />
                <Group label="Gaps" items={result.gaps} />
                <Group label="Unclear items" items={result.unclear_items} />
                <Group label="Follow-up questions" items={result.follow_up_questions} />
                <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-2 text-[11px] text-amber-900">
                  AI never rejects, selects, advances or submits a candidate. Human review is required.
                </div>
              </div>
            ) : <div className="text-xs text-muted-foreground">Run the analysis to see AI output.</div>}
          </Pane>
        </div>
      )}

      {result && (
        <Card className="p-5 animate-fade-up" data-testid="ai-match-result">
          <div className="flex items-center gap-4 mb-4">
            <div className="h-20 w-20 rounded-full grid place-items-center border-4 border-[#255058]/20">
              <span className="font-display text-2xl font-bold text-[#255058]">{result.score}</span>
            </div>
            <div><div className="font-display font-semibold text-lg">{result.verdict}</div><p className="text-sm text-muted-foreground max-w-xl mt-1">{result.reasoning}</p></div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div><div className="text-xs font-semibold uppercase text-[#2f9e5f] mb-2">Strengths</div><ul className="space-y-1 text-sm">{(result.strengths || []).map((s, i) => <li key={i}>• {s}</li>)}</ul></div>
            <div><div className="text-xs font-semibold uppercase text-[#c65a63] mb-2">Gaps</div><ul className="space-y-1 text-sm">{(result.gaps || []).map((s, i) => <li key={i}>• {s}</li>)}</ul></div>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {(result.matched_requirements || []).map((s, i) => <Badge key={i} className="status-green border-0">{s}</Badge>)}
            {(result.missing_requirements || []).map((s, i) => <Badge key={`m${i}`} className="status-red border-0">{s}</Badge>)}
          </div>
        </Card>
      )}
    </div>
  );
}
